import { useCallback, useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { BookIcon } from 'lucide-react';
import { Controller, Resolver, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import * as yup from 'yup';

import ContextualHelp from '@/components/atoms/ContextualHelp/ContextualHelp';
import IconizedDialog from '@/components/atoms/IconizedDialog/IconizedDialog';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import useBrains from '@/hooks/useBrains';
import useDialogAlert from '@/hooks/useDialogAlert';
import useDialogNodesCache from '@/hooks/useDialogNodesCache';
import useDialogs from '@/hooks/useDialogs';
import useFocusOnInput from '@/hooks/useFocusOnInput';
import { useIsStartingNode } from '@/hooks/useIsStartingNode';
import { cn } from '@/lib/utils';
import { languageSupportsEnhance } from '@/redux/dialogs/helper';
import { selectSelectedNode } from '@/redux/dialogs/selectors';
import { selectFeatureFlags } from '@/redux/featureFlag/selectors';
import { updateNode } from '@/redux/nodes/actions';
import { selectAccountId, selectAccountSlug, selectBrainId } from '@/redux/session/selectors';
import { RootState } from '@/shared/types/state';
import { EVENT_RESPONSE_DOCS_URL } from '@/util/constants';
import { formatErrorMessage } from '@/util/util';
import { MAX_DIALOG_NAME_LENGTH } from '@/util/validation/constants';

import { nodeType } from './util';
import ToolkitWrapper from '../../ToolkitWrapper';
import { AssignedReminders } from '../AssignedReminders';
import { StartingDialogBanner } from '../StartingDialogBanner/StartingDialogBanner';

type Inputs = { name: string; enhance: boolean };

export const nodeEventSchema = yup
  .object()
  .shape({
    name: yup.string().max(MAX_DIALOG_NAME_LENGTH).required(),
    enhance: yup.boolean().required(),
  });

const ToolkitNodeEvent = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { nodeId, name, enhance } = useSelector((state: RootState) => {
    const selectedNode = selectSelectedNode(state);
    return {
      nodeId: selectedNode.node_id,
      name: selectedNode.name ?? '',
      enhance: selectedNode?.enhance ?? false,
    };
  }, shallowEqual);
  const navigate = useNavigate();
  const slug = useSelector(selectAccountSlug);
  const brainId = useSelector(selectBrainId);
  const { findUsedNodes } = useDialogNodesCache();
  const usedIn = findUsedNodes({ brainId, name: nodeId });
  const nodesLength = usedIn?.length;
  const { dialogs } = useDialogs(brainId);

  const { brain } = useBrains(brainId);
  const accountId = useSelector(selectAccountId);
  const features = useSelector(selectFeatureFlags);

  const showAIEnhancement = languageSupportsEnhance(brain?.language, accountId, features);
  const isStartingNode = useIsStartingNode(nodeId);

  const formMethods = useForm<Inputs>({
    mode: 'onChange',
    defaultValues: { name, enhance },
    resolver: yupResolver(nodeEventSchema) as Resolver<Inputs>,
  });
  const {
    formState: { errors },
    getValues,
    trigger,
    setFocus,
    control,
  } = formMethods;

  useFocusOnInput('name', errors, setFocus);

  const handleChange = useCallback(() => {
    const values = getValues();

    dispatch(updateNode({ nodeId, node: { ...values } }));
  }, [getValues, dispatch, nodeId]);

  const { setFieldError } = useDialogAlert({
    id: nodeId,
    nodeId,
    title: t('dialog.node_event'),
    type: 'event',
  });

  useEffect(() => {
    setFieldError('name', formatErrorMessage(errors.name));
  }, [errors.name, setFieldError]);

  useEffect(() => {
    // Trigger validation on mount
    trigger();
  }, [trigger]);

  return (
    <ToolkitWrapper type="event">
      <Form {...formMethods}>
        <FormField
          key={nodeId}
          control={control}
          name="name"
          render={({ field }) => (
            <FormItem className="mb-6">
              <FormLabel>{t('common.name')}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  onChange={(event) => {
                    field.onChange(event);
                    handleChange();
                  }}
                />
              </FormControl>
              <FormMessage>{formatErrorMessage(errors.name)}</FormMessage>
            </FormItem>
          )}
        />
      </Form>
      <ContextualHelp
        title={t('dialog.event.contextual_title')}
        name="about_events"
        preventClose
        links={[
          { label: t('docs.docs'), url: EVENT_RESPONSE_DOCS_URL, icon: <BookIcon size={16} /> },
        ]}
      >
        {t('dialog.event.contextual_message')}
      </ContextualHelp>

      {
        <div className="my-6">
          <AssignedReminders />
        </div>
      }

      {showAIEnhancement && (
        <Controller
          name="enhance"
          control={control}
          render={({ field: { value, onChange: onControllerChange } }) => {
            return (
              <InfoTooltip asChild={false} content={t('dialog.unknown.enhance_with_ai_tooltip')}>
                <Switch
                  label={t('dialog.unknown.enhance_with_ai')}
                  checked={value}
                  onCheckedChange={(newValue) => {
                    onControllerChange(newValue);
                    handleChange();
                  }}
                />
              </InfoTooltip>
            );
          }}
        />
      )}

      {nodesLength > 0 && (
        <div className="mt-8">
          <div className="mb-1">
            <span className="text-sm leading-5 font-medium text-foreground-muted">
              {t('dialog.event.used_by')}
            </span>
          </div>
          <div className="mb-3">
            <span className="text-sm leading-5 font-normal text-foreground-muted">
              <Trans i18nKey="dialog.event.jump_to" />
            </span>
          </div>
          <div className="flex max-h-80 flex-col items-start overflow-y-auto pt-1 [&_path]:fill-current [&_path]:stroke-current">
            {usedIn.map((x) => (
              <button
                key={`${x.nodeId}-${x.label}`}
                type="button"
                onClick={() =>
                  navigate(`/${slug}/ai-agents/${brainId}/workflows/dialogs/${x.dialogId}`)
                }
                className={cn('ml-1.5 flex items-center gap-1 bg-inherit pb-1 text-informational', {
                  '[&_path]:!fill-none': nodeType(x.dialogId, dialogs) === 'event',
                })}
              >
                <IconizedDialog
                  key={x.nodeId}
                  dialogName={x.label}
                  type={nodeType(x.dialogId, dialogs)}
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {isStartingNode && <StartingDialogBanner />}
    </ToolkitWrapper>
  );
};

export default ToolkitNodeEvent;
